'use client';

import posthog from 'posthog-js';
import { resolvePostHogDeploymentProperties } from '../deployment-environment';
import {
  buildPostHogInitConfig,
  scrubPostHogInitialPersonInfo,
} from './posthog-browser-config';
import { createInternalTestWorkspaceSync } from './internal-test-workspace';
import { isPostHogCaptureAllowed } from './posthog-capture-gate';
import {
  createReplayRouteGuard,
  isReplayBlockedPath,
} from './replay-route-guard';

const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const posthogHost =
  process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';
const posthogUiHost = posthogHost.replace('.i.posthog.com', '.posthog.com');

export const posthogDeploymentProperties = resolvePostHogDeploymentProperties({
  convexUrl: process.env.NEXT_PUBLIC_CONVEX_URL,
  hostname:
    typeof window === 'undefined' ? undefined : window.location.hostname,
  imaiEnvironment: process.env.NEXT_PUBLIC_IMAI_ENV,
  vercelGitCommitRef: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF,
  vercelGitCommitSha: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
  vercelTargetEnvironment:
    process.env.NEXT_PUBLIC_VERCEL_TARGET_ENV ||
    process.env.NEXT_PUBLIC_VERCEL_ENV,
});

let isInitialized = false;
let replayGuard: { sync: (pathname: string) => void } | null = null;

let flagsDeferred = false;
let syncInternalTestWorkspace: ((pathname: string) => unknown) | null = null;

/**
 * On every route change, before paint: stop replay on capability routes and
 * resume it elsewhere, keep the stored first-page info redacted, and load
 * the flags a capability first page held back once the person leaves it.
 */
export const syncPostHogReplayForPath = (pathname: string): void => {
  if (!isInitialized) return;
  replayGuard?.sync(pathname);
  syncInternalTestWorkspace?.(pathname);
  scrubPostHogInitialPersonInfo(posthog, {
    href: window.location.href,
    referrer: document.referrer,
  });
  if (flagsDeferred && !isReplayBlockedPath(pathname)) {
    flagsDeferred = false;
    posthog.reloadFeatureFlags();
  }
};
let missingKeyWarningEmitted = false;

export const registerPostHogDeploymentProperties = (): void => {
  posthog.register(posthogDeploymentProperties);
  // posthog.reset() drops super properties, so re-tag the current route.
  syncInternalTestWorkspace = createInternalTestWorkspaceSync(posthog);
  syncInternalTestWorkspace(window.location.pathname);
};

export const ensurePostHogBrowserInitialized = (): boolean => {
  if (isInitialized) return true;
  if (typeof window === 'undefined') return false;

  const isLocalhost =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname === '::1';
  const shouldInitialize = isPostHogCaptureAllowed({
    apiKey: posthogKey,
    hostname: window.location.hostname,
    enableLocalAnalytics:
      process.env.NEXT_PUBLIC_ENABLE_LOCAL_ANALYTICS === 'true',
  });

  // The predicate already requires a key; the second check narrows the type.
  if (!shouldInitialize || !posthogKey) {
    if (!isLocalhost && !posthogKey && !missingKeyWarningEmitted) {
      console.warn(
        '[PostHog] NEXT_PUBLIC_POSTHOG_KEY is not set; PostHog analytics are disabled.'
      );
      missingKeyWarningEmitted = true;
    }
    return false;
  }

  // Replay never starts on a capability route (sharing review r7 #2).
  const startedBlocked = isReplayBlockedPath(window.location.pathname);
  replayGuard = createReplayRouteGuard(posthog, { startedBlocked });
  flagsDeferred = startedBlocked;
  posthog.init(
    posthogKey,
    buildPostHogInitConfig({
      pathname: window.location.pathname,
      uiHost: posthogUiHost,
    })
  );
  registerPostHogDeploymentProperties();
  isInitialized = true;
  return true;
};

export { posthog };
